import { useCallback } from 'react';
import confetti from 'canvas-confetti';

// Custom confetti configurations
const confettiConfigs = {
  // Celebration for favorites
  favorite: {
    particleCount: 50,
    spread: 60,
    origin: { y: 0.7 },
    colors: ['#FF6B6B', '#FF8E8E', '#FFB3B3', '#FFC857', '#FF6B35'],
    shapes: ['circle', 'square'],
    gravity: 1.2,
    scalar: 0.9,
    ticks: 100,
  },
  
  // Big celebration for bookings/reservations
  booking: {
    particleCount: 150,
    spread: 100,
    startVelocity: 45,
    origin: { y: 0.6 },
    colors: ['#00C2D1', '#FFC857', '#FF6B35', '#8B5CF6', '#22C55E'],
    shapes: ['circle', 'square'],
    gravity: 0.8,
    scalar: 1,
    ticks: 200,
  },
  
  // Gold/Diamond celebration
  diamond: {
    particleCount: 100,
    spread: 70,
    startVelocity: 30,
    origin: { y: 0.5 },
    colors: ['#FFC857', '#FFE9A3', '#F59E0B', '#FBBF24', '#FDE68A'],
    shapes: ['circle'],
    gravity: 0.6,
    scalar: 1.2,
    ticks: 150,
  },
  
  // Success celebration
  success: {
    particleCount: 80,
    spread: 55,
    origin: { y: 0.65 },
    colors: ['#22C55E', '#4ADE80', '#86EFAC', '#00C2D1', '#67E8F9'],
    shapes: ['circle', 'square'],
    gravity: 1,
    scalar: 1,
    ticks: 120,
  },
  
  // Side cannons effect
  sideCannons: {
    particleCount: 30,
    angle: 60,
    spread: 55,
    startVelocity: 60,
    colors: ['#FFC857', '#00C2D1', '#FF6B35', '#8B5CF6'],
    gravity: 1,
    scalar: 1,
    ticks: 150,
  },
};

export const useConfetti = () => {
  // Fire confetti with a specific configuration
  const fireConfetti = useCallback((type = 'favorite', customConfig = {}) => {
    const config = { ...confettiConfigs[type], ...customConfig };
    confetti(config);
  }, []);
  
  // Fire confetti from a specific element
  const fireFromElement = useCallback((element, type = 'favorite', customConfig = {}) => {
    if (!element) return;
    
    const rect = element.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;
    
    const config = { 
      ...confettiConfigs[type], 
      origin: { x, y },
      ...customConfig 
    };
    
    confetti(config);
  }, []);
  
  // Fire side cannons effect (from both sides)
  const fireSideCannons = useCallback((customConfig = {}) => {
    const config = { ...confettiConfigs.sideCannons, ...customConfig };
    
    // Left side
    confetti({
      ...config,
      angle: 60,
      origin: { x: 0 },
    });
    
    // Right side
    confetti({
      ...config,
      angle: 120,
      origin: { x: 1 },
    });
  }, []);
  
  // Fire continuous confetti for a duration
  const fireConfettiRain = useCallback((duration = 3000, type = 'booking') => {
    const end = Date.now() + duration;
    const colors = confettiConfigs[type]?.colors || ['#FFC857', '#00C2D1'];
    
    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0 },
        colors,
      });
      
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0 },
        colors,
      });
      
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    
    frame();
  }, []);
  
  // Fire realistic confetti burst
  const fireRealistic = useCallback(() => {
    const count = 200;
    const defaults = {
      origin: { y: 0.7 },
    };

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
      });
    }

    fire(0.25, {
      spread: 26,
      startVelocity: 55,
      colors: ['#FFC857', '#FFE9A3'],
    });
    
    fire(0.2, {
      spread: 60,
      colors: ['#00C2D1', '#67E8F9'],
    });
    
    fire(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8,
      colors: ['#FF6B35', '#F97316'],
    });
    
    fire(0.1, {
      spread: 120,
      startVelocity: 25,
      decay: 0.92,
      scalar: 1.2,
      colors: ['#8B5CF6', '#A78BFA'],
    });
    
    fire(0.1, {
      spread: 120,
      startVelocity: 45,
      colors: ['#22C55E', '#4ADE80'],
    });
  }, []);
  
  // Fire heart-shaped confetti for favorites
  const fireHearts = useCallback((origin = { x: 0.5, y: 0.7 }) => {
    // Create heart shape using multiple bursts
    const colors = ['#FF6B6B', '#FF8E8E', '#FFB3B3', '#FFC0CB'];
    
    for (let i = 0; i < 3; i++) {
      setTimeout(() => {
        confetti({
          particleCount: 20,
          spread: 50,
          origin,
          colors,
          shapes: ['circle'],
          scalar: 0.8,
          gravity: 1.5,
          ticks: 80,
        });
      }, i * 100);
    }
  }, []);
  
  // Fire stars for premium/diamond features
  const fireStars = useCallback((origin = { x: 0.5, y: 0.5 }) => {
    const colors = ['#FFC857', '#FFE9A3', '#FBBF24'];
    
    confetti({
      particleCount: 30,
      spread: 360,
      startVelocity: 20,
      origin,
      colors,
      shapes: ['star'],
      scalar: 1.5,
      gravity: 0.5,
      ticks: 100,
    });
  }, []);

  return {
    fireConfetti,
    fireFromElement,
    fireSideCannons,
    fireConfettiRain,
    fireRealistic,
    fireHearts,
    fireStars,
  };
};

export default useConfetti;
